import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import Moment from 'react-moment';
import { Link, useRouteMatch } from 'react-router-dom';

import { MessageContext } from '../../contexts/messageContext';
import { GET_ROOM_getRoom_messages_author } from '../../graphql/types/GET_ROOM';
import {
  GET_USER_getUser,
  GET_USER_getUser_professions
} from '../../graphql/types/GET_USER';
import { calendarStrings } from '../../utils/dateTime';
import { getCompressedImagePath } from '../../utils/getCompressedImagePath';
import { PrettyContent } from '../Content';
import { MessageContentLoader } from '../ContentLoader';
import MessageReplyForm from './MessageReplyForm';
import CoffeeChatModal from './CoffeeChatModal';
import ImageLightbox from './ImageLightbox';
import InfiniteScroll from 'react-infinite-scroll-component';
import MatchiChatLogo from '../../assets/images/matchi/matchi-chat.png';
import Spinner from '../Spinner';

const DateSeparator = ({ date }: { date: string }) => (
  <div className="date-separator">
    <Moment calendar={calendarStrings}>{date}</Moment>
  </div>
);

interface IMessageImage {
  id: string;
  fileUrl: string;
}

interface IMessageItemProps {
  content: string;
  createdAt: string;
  author: GET_ROOM_getRoom_messages_author;
  otherUser: GET_USER_getUser;
  image?: IMessageImage | null;
  onImageClick?: (imageUrl: string) => void;
}

interface IMessage {
  id: string;
  content: string;
  createdAt: string;
  readAt?: string;
  author: GET_ROOM_getRoom_messages_author;
  roomId?: string;
  image?: IMessageImage | null;
}

const MessageItem = React.memo(({ author, createdAt, content, otherUser, image, onImageClick }: IMessageItemProps) => {
  const isAuthoredByCurrentUser = author.id !== otherUser.id;
  const avatar = author.avatar
    ? author.avatar && author.avatar.fileUrl
    : otherUser.avatar && otherUser.avatar.fileUrl;

  return (
    <div
      className={`message-item ${
        isAuthoredByCurrentUser ? 'current-user' : ''
      }`}
    >
      {!isAuthoredByCurrentUser && (
        <img
          src={getCompressedImagePath(avatar, 'thumbnail') || avatar}
          alt="profile"
          className="message-avatar"
        />
      )}
      <div className="message-content">
        {image && (
          <div style={{ marginBottom: content ? '8px' : '0' }}>
            <img
              src={image.fileUrl}
              alt="Shared"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                borderRadius: '8px',
                cursor: 'pointer',
                objectFit: 'cover'
              }}
              onClick={() => onImageClick?.(image.fileUrl)}
            />
          </div>
        )}
        {content && (
          <PrettyContent
            content={content}
            isLineBreakEnabled={true}
            isCurrentUser={isAuthoredByCurrentUser ? 'current-user' : ''}
          />
        )}
        <div className="message-time">
          <Moment format="h:mm A">{createdAt}</Moment>
        </div>
      </div>
      {isAuthoredByCurrentUser && (
        <img
          src={getCompressedImagePath(avatar, 'thumbnail') || avatar}
          alt="profile"
          className="message-avatar"
        />
      )}
    </div>
  );
});

interface IRoomParams {
  roomId?: string;
}

const getLocalDate = (dateString: string) => {
  // Parse the date string and adjust for local timezone
  const date = new Date(dateString.replace(' ', 'T'));
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date(date.toLocaleString('en-US', { timeZone: userTimezone }));
};

interface IMessageContentProps {
  onUserLoaded?: (user: any) => void;
  onRoomLoaded?: (chatType: string | null) => void;
}

const MessageContent = ({ onUserLoaded, onRoomLoaded }: IMessageContentProps) => {
  const messageListRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [showCoffeeChatModal, setShowCoffeeChatModal] = useState(false);
  const [isAcceptingCoffeeChat, setIsAcceptingCoffeeChat] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const { actions, currentRoom, currentUser } = useContext(MessageContext);
  const { params } = useRouteMatch<IRoomParams>();

  // Get messages directly from Apollo cache via currentRoom
  const messages = currentRoom.data?.getRoom?.messages || [];
  const room = currentRoom.data?.getRoom;
  const isCoffeeChatRoom = room?.chatType === 'coffee';

  const memoizedGroupMessagesByDate = React.useCallback((messages: IMessage[]) => {
    const groups = new Map();
    const sortedMessages = [...messages].sort((a, b) => {
      const dateA = getLocalDate(a.createdAt);
      const dateB = getLocalDate(b.createdAt);
      return dateA.getTime() - dateB.getTime();
    });
    sortedMessages.forEach((message) => {
      const localDate = getLocalDate(message.createdAt);
      const dateKey = localDate.toLocaleDateString('en-CA');
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey).push({
        ...message,
        localizedDate: localDate
      });
    });
    return groups;
  }, []);

  const memoizedRenderMessages = React.useCallback(() => {
    if (!messages || !otherUser) return null;

    const messageGroups = memoizedGroupMessagesByDate(messages);
    const sortedDates = Array.from(messageGroups.keys()).sort((a, b) => a.localeCompare(b));

    return sortedDates.map((date) => (
      <Fragment key={date}>
        <DateSeparator date={date} />
        {messageGroups.get(date).map((message, idx) => (
          <MessageItem
            key={`${message.id}-${idx}`}
            {...message}
            otherUser={otherUser}
            onImageClick={setLightboxImage}
          />
        ))}
      </Fragment>
    ));
  }, [messages, otherUser, memoizedGroupMessagesByDate]);

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    const isAtBottom = container.scrollHeight - container.scrollTop === container.clientHeight;

    if (isAtBottom) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    } else {
      const prevScrollPos = scrollPositionRef.current;
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollPos;
        }
      });
    }
  }, [messages]);

  useEffect(() => {
    if (currentRoom.data?.getRoom?.messages) {
      const user = actions.getOtherUser(currentRoom.data.getRoom.members);
      setOtherUser(user);

      if (onUserLoaded && user) {
        onUserLoaded(user);
      }
    }
  }, [currentRoom.data?.getRoom?.members, actions, onUserLoaded]);

  useEffect(() => {
    if (params.roomId) {
      actions.handleLoadRoom(params.roomId);

      return () => {
        actions.stopPolling();
      };
    }
  }, [params.roomId, actions.handleLoadRoom, actions.stopPolling]);

  // Coffee chat modal logic
  useEffect(() => {
    if (isCoffeeChatRoom && otherUser && room && !currentRoom.loading) {
      // Show modal in different scenarios:
      // 1. Person B (receiver) when they first enter
      // 2. Both users when coffee chat is accepted (both-accepted mode)
      if (shouldShowCoffeeChatModal()) {
        setShowCoffeeChatModal(true);
      } else if (shouldShowBothAcceptedModal()) {
        setShowCoffeeChatModal(true);
        localStorage.setItem(`coffee-chat-success-${room.id}`, 'shown');
      }
    }
  }, [isCoffeeChatRoom, otherUser, room, messages, currentRoom.loading, actions]);

  useEffect(() => {
    if (room && onRoomLoaded) {
      onRoomLoaded(room.chatType || null);
    }
  }, [room?.chatType, onRoomLoaded]);

  // Update readAtMessages when messages comes in - memoized to prevent loops
  const handleReadAtMessages = React.useCallback(() => {
    if (
      currentRoom.data?.getRoom?.messages?.length > 0 &&
      !currentRoom.loading &&
      params.roomId
    ) {
      const unreadMessages = currentRoom.data.getRoom.messages
        .filter(m => !m.readAt)
        .map(m => Number(m.id));

      if (unreadMessages.length > 0) {
        actions.handleReadAtMessages(
          unreadMessages,
          Number(currentRoom.data.getRoom.id)
        );
      }
    }
  }, [
    currentRoom.data?.getRoom?.messages?.length,
    currentRoom.data?.getRoom?.id,
    currentRoom.loading,
    params.roomId
  ]);

  // Debounced read at messages to prevent excessive calls
  useEffect(() => {
    const timer = setTimeout(() => {
      handleReadAtMessages();
    }, 500); // Wait 500ms before marking as read

    return () => clearTimeout(timer);
  }, [currentRoom.data?.getRoom?.messages]);

  if (currentRoom.loading) {
    return <MessageContentLoader />;
  }

  if (!currentRoom.loading && currentRoom.error) {
    return <div className="notification is-danger">Something went wrong.</div>;
  }

  const handleLoadMore = async () => {
    if (!params.roomId || currentRoom.loading || !currentRoom.hasMore) {
      return null;
    }
    const container = messageListRef.current;
    if (container) {
      scrollPositionRef.current = container.scrollHeight - container.scrollTop;
    }

    try {
      const response = await actions.loadMoreMessages(params.roomId);
      if (!response?.data?.getRoom?.messages?.length) {
        actions.setHasMore(false);
        return null;
      }
      return response;
    } catch (error) {
      console.error('Error loading more messages:', error);
    }
  };

  const handleCoffeeChatAction = async () => {
    if (!room?.id) return;

    const mode = getCoffeeChatMode();

    if (mode === 'accept') {
      // Person B accepting the coffee chat
      setIsAcceptingCoffeeChat(true);
      try {
        const success = await actions.handleAcceptCoffeeChat(room.id);
        if (success) {
          setShowCoffeeChatModal(false);
        }
      } catch (error) {
        console.error('Error accepting coffee chat:', error);
      } finally {
        setIsAcceptingCoffeeChat(false);
      }
    } else if (mode === 'both-accepted') {
      // Both users accepted - close modal and start chatting
      setShowCoffeeChatModal(false);
    }
  };

  const handleRequestAnother = () => {
    setShowCoffeeChatModal(false);
  };

  const getCoffeeChatMode = () => {
    if (!room?.id) return 'request';

    const isAccepted = room.isAccepted;

    // If coffee chat is accepted
    if (isAccepted) {
      return 'both-accepted';
    }

    // Person A (requester/owner) should never see accept mode
    const isRequester = isCurrentUserRequester();
    if (isRequester) {
      return 'request';
    }

    // Person B (receiver/non-owner) sees accept mode when not accepted
    if (!isRequester && !isAccepted) {
      return 'accept';
    }

    return 'request';
  };

  const isCurrentUserRequester = () => {
    if (!isCoffeeChatRoom || !room?.id || !room?.owner) return false;

    // Person A is the requester if they are the room owner
    return room.owner.id === currentUser.id;
  };

  const shouldShowCoffeeChatModal = () => {
    if (!isCoffeeChatRoom || !room?.id) return false;

    // Only show modal for Person B (non-owner) when they first enter and haven't accepted
    const isRequester = isCurrentUserRequester();
    const isAccepted = room.isAccepted;

    return !isRequester && !isAccepted;
  };

  const shouldShowBothAcceptedModal = () => {
    if (!isCoffeeChatRoom || !room?.id) return false;

    const isAccepted = room.isAccepted;

    // Show both-accepted modal once when coffee chat is accepted
    if (isAccepted) {
      const hasShownSuccess = localStorage.getItem(`coffee-chat-success-${room.id}`) === 'shown';
      return !hasShownSuccess;
    }

    return false;
  };

  const shouldShowMessageInput = () => {
    if (!isCoffeeChatRoom || !room?.id) return true;

    // If coffee chat is accepted, both parties can chat
    return room.isAccepted;
  };

  const shouldShowRequestSent = () => {
    if (!isCoffeeChatRoom || !room?.id) return false;

    // Show "Request sent!" for Person A (owner) until Person B accepts
    const isRequester = isCurrentUserRequester();
    const isAccepted = room.isAccepted;
    return isRequester && !isAccepted;
  };

  const shouldShowAcceptButton = () => {
    if (!isCoffeeChatRoom || !room?.id) return false;

    // Show accept button for Person B (non-owner) until they accept
    const isRequester = isCurrentUserRequester();
    const isAccepted = room.isAccepted;
    return !isRequester && !isAccepted;
  };

  return (
    <Fragment>
      {/* Image Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage ?? ''}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />

      {/* Coffee Chat Modals */}
      {isCoffeeChatRoom && otherUser && (
        <CoffeeChatModal
          isOpen={showCoffeeChatModal}
          onClose={() => setShowCoffeeChatModal(false)}
          matchName={otherUser.profile?.name || 'Unknown'}
          matchAvatar={otherUser.avatar?.fileUrl || ''}
          matchLocation={otherUser.profile?.city || otherUser.profile?.location}
          matchBio={otherUser.profile?.bio}
          onAccept={handleCoffeeChatAction}
          onRequestAnother={handleRequestAnother}
          mode={getCoffeeChatMode()}
          isLoading={isAcceptingCoffeeChat}
        />
      )}

      {/* Header message content */}
      <div className="level page-messages-column-content-header is-bordered-bottom m-b-0">
        {otherUser && otherUser.profile && (
          <div className="level-left">
            <div className="level-item">
              <article className="media align-items-left">
                <figure className="media-left">
                  <img
                    src={getCompressedImagePath(otherUser.avatar.fileUrl, 'thumbnail') || otherUser.avatar.fileUrl}
                    alt="profile"
                    className="image is-inline is-rounded-circle is-48x48"
                  />
                </figure>
                <div className="media-content">
                  <div className="content">
                    <Link to={`/user/${otherUser.id}`}>
                      <p className="has-text-weight-semibold">
                        <span className='p-r-1'>{otherUser.profile.name}</span>
                        {isCoffeeChatRoom && <span className="tag is-primary is-small coffee-chat-badge">Coffee Chat</span>}
                      </p>
                    </Link>
                    <p>
                      {otherUser.professions &&
                        otherUser.professions.map(
                          (
                            profession: GET_USER_getUser_professions,
                            key: number
                          ) => (
                            <span key={key} className="is-small is-light">
                              {(key ? ', ' : '') + profession.name}
                            </span>
                          )
                        )}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        )}
        {isCoffeeChatRoom && (
          <div className="level-right">
            <div className="level-item">
              <img
                src={MatchiChatLogo}
                alt="Matchi Chat"
                className="matchi-chat-logo"
                style={{ width: '48px', height: '48px' }}
              />
            </div>
          </div>
        )}
      </div>
      {currentRoom.data && currentRoom.data.getRoom && otherUser && (
        <div
          className="card-content page-messages-column-card-content p-t-1 p-b-0 p-l-0"
          ref={messageListRef}
          id="messages-container"
          style={{
            height: 'calc(100vh - 200px)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column-reverse'
          }}
        >
          <InfiniteScroll
              dataLength={messages.length}
              next={handleLoadMore}
              hasMore={currentRoom.hasMore}
              loader={null}
              inverse={true}
              style={{ display: 'flex', flexDirection: 'column-reverse' }}
              scrollableTarget="messages-container"
              scrollThreshold={0.8}
              className="messages-scroll-container"
              initialScrollY={messageListRef.current?.scrollHeight ?? 0}
              endMessage={null}
            >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {memoizedRenderMessages()}
            </div>
          </InfiniteScroll>
        </div>
      )}
      {/* Show MessageReplyForm only if it's not a coffee chat or if coffee chat has been accepted */}
      {shouldShowMessageInput() ? (
        <MessageReplyForm />
      ) : shouldShowRequestSent() ? (
        <div className="coffee-chat-request-container" style={{ padding: '1rem', borderTop: '1px solid #eee' }}>
          <div
            className="button is-primary is-fullwidth"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'default',
              opacity: 0.7
            }}
          >
            <span>☕</span>
            <span>Request sent! Waiting for response...</span>
          </div>
        </div>
      ) : shouldShowAcceptButton() ? (
        <div className="coffee-chat-request-container" style={{ padding: '1rem', borderTop: '1px solid #eee' }}>
          <button
            className="button is-primary is-fullwidth"
            onClick={handleCoffeeChatAction}
            disabled={isAcceptingCoffeeChat}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isAcceptingCoffeeChat ? (
              <>
                <Spinner />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>☕</span>
                <span>Accept Coffee Chat</span>
              </>
            )}
          </button>
        </div>
      ) : null}
    </Fragment>
  );
};

export default React.memo(MessageContent);
